<template>
  <div class="eligibility-uploader">
    <div class="import-card">
      <div class="card-header">
        <h2>Eligibility List Upload Tool</h2>
        <md-button @click="handleTemplateDownload">
          <i>Download Template File</i>
        </md-button>
      </div>
      <div class="card-body">
        <div class="instructions">
          <div class="bold">You have two options for updating your list of eligible unique IDs: (1) manual upload tool (described below) and (2) the automated SFTP feed (see the SFTP Feed tab).</div>
          <br/>
          <br/>
          <h5>What to upload</h5>
          <ul class="copy-list">
            <li>
              Each time you upload a file, please ensure you are uploading the
              <div class="emphasis">full</div>
              list of unique IDs eligible to request funds from the Grant
              Circle. You may download the existing file of eligible IDs by
              clicking the "Download current eligibility list" button on the
              SFTP Feed page.
            </li>
            <li>
              The file should
              <div class="emphasis">have two columns:</div>
              column A, containing the list of eligible unique IDs, and column B, containing the
              list of email addresses corresponding to each unique ID. Email addresses are used to
              send a notification of account creation for security purposes. If you do not have
              email addresses for all unique IDs, blanks are accepted, though individuals for whom
              we do not have an email will not receive the security notification upon account
              creation. (Note: All users will receive the standard account creation notification to
              the email they provide during account creation.)
            </li>
            <li>
              Column A should have a header of
              <div class="emphasis">Unique ID,</div>
              and column B should have a header of
              <div class="emphasis">Email Address.</div> Make sure there are no extra spaces before or after the phrases in the header labels.
              (Values for each row in column B are optional.)
            </li>
            <li>
              Make sure there are no unnecessary spaces before or after the ID values in Column A.
            </li>
            <li>There should only be one unique ID per row.</li>
            <li>There should not be any duplicate unique IDs or email addresses.</li>
            <li>
              The file should be in CSV format. If you are working in Excel, select ‘save as’ and
              choose CSV as the format.
            </li>
            <li>
              Please note that Excel may change the formatting of your unique
              IDs. In particular, a common problem is the removal of leading
              zeros from unique IDs containing only numbers.
            </li>
          </ul>
          <h5>How it works</h5>
          <ul class="copy-list">
            <li>
              Each uploaded list will be compared to the list of eligible IDs already in the
              database.
              <ul>
                <li>
                  New IDs, never before on the list: will be added to the eligible list in the
                  database.
                </li>
                <li>
                  New IDs, previously onboarded, removed from list, and marked inactive: will be
                  added to the eligible list and marked as active, reinstating the individual's
                  ability to log in, request, and receive funds, given previous activity.
                </li>
                <li>
                  Missing IDs, not yet onboarded: will be removed from the eligible list in the database.
                </li>
                <li>
                  Missing IDs, already onboarded and active: will be marked as inactive in the
                  database, preventing individuals from logging in, requesting, and receiving funds.
                </li>
                <li>
                  Existing IDs (common to both new and existing lists): will remain unchanged.
                </li>
                <li>
                  Existing IDs with new or different email address: will remain on the list and email address will be updated.
                </li>
                <li>
                  If an ID is on the list more than once, each time associated with the same email value, it will only be counted once.
                </li>
                <li>
                  If duplicates in column A are matched with different values in column B, the list will generate an error.
                </li>
                <li>
                  If duplicates in column B are matched with different values in column A, the list will generate an error.
                </li>
              </ul>
            </li>
            <li>
              After a new list is uploaded, we will tell you the results of the comparison (number
              of IDs to be added, number to be removed, number to be marked as inactive, and the
              number to be reinstated as active). We will ask for your confirmation prior to
              adjusting the list in the database to avoid unintended errors caused by faulty list
              formatting.
            </li>
            <li>
              You may view the eligibility list update history on the bottom of the SFTP Feed page.
            </li>
          </ul>
        </div>
        <div class="selections-container">
          <div v-if="selectedFile" class="current-file">
            <span class="label">Selected File:</span>
            <span class="file-name">{{ selectedFile.name }}</span>
          </div>
          <select
            v-if="user.isStaff && !organizations.refreshing"
            class="organizations"
            @change="onOrganizationSelection"
          >
            <option value="" disabled :selected="selectedOrganization.id === null || selectedOrganization.id === undefined"
              >Select Organization</option
            >
            <option
              v-for="org in organizations.list"
              :key="org.id"
              :value="org.id"
              :selected="selectedOrganization.id === org.id"
              >{{ org.name }}</option
            >
          </select>
          <div
            v-else-if="user.isStaff && organizations.refreshing"
            class="organizations placeholder"
          >
            <LoadSpinner />
          </div>
          <select
            v-if="(user.isAdmin && !user.subOrganization && suborganizations().length) || user.isStaff && suborganizations().length"
            class="suborganizations"
            @change="onSubOrganizationSelection"
          >
            <option value="" disabled :selected="selectedSubOrganization.id === null || selectedOrganization.id === undefined"
              >Select Sub Organization</option
            >
            <option
              v-for="sub_org in suborganizations()"
              :key="sub_org.id"
              :value="sub_org.id"
              :selected="selectedSubOrganization.id === sub_org.id"
              >{{ sub_org.name }}</option
            >
          </select>
        </div>
        <div v-if="errors" class="errors-container">
          <p v-for="error in errors" :key="error" class="error">{{ error }}</p>
        </div>
        <div class="buttons-container">
          <md-button
            class="choose-file-button"
            @click="handleUploadClick"
            :disabled="isUploadDisabled"
          >
            <input type="file" accept=".csv" hidden ref="file" @change="handleUpload" />
            <i>Choose File</i>
          </md-button>
          <md-button
            v-if="isProceedRendered"
            class="proceed-button"
            @click="onDryRun"
            :disabled="isProceedDisabled"
          >
            <i v-if="!dryRunLoading">Proceed</i>
            <span v-else>
              <LoadSpinner :color="'white'" />
            </span>
          </md-button>
        </div>
      </div>
    </div>
    <Modal v-if="modal.isOpen" dimmed @close-modal="modal.isOpen = false">
      <div class="dry-results">
        <h2>Please Confirm</h2>
        <div class="top-margin" v-if="user.isStaff">
          For Organization:
          <div class="emphasis">{{ selectedOrganization.name }}</div>
        </div>
        <div class="top-margin">The file you uploaded contains:</div>
        <ul>
          <li v-if="failedForErrors(dryRun)">
            <div class="emphasis">{{ dryRun.errors.length }}</div>
            errors
            <template v-if="dryRun.errors.length">
              <div
                :style="{ display: 'inline' }"
                v-if="modal.showErrors"
                @click="modal.showErrors = false"
                >
                <md-icon>
                  visibility
                </md-icon>
              </div>
              <div :style="{ display: 'inline' }" v-else @click="modal.showErrors = true">
                <md-icon>
                  visibility_off
                </md-icon>
              </div>
            </template>
            <ol v-if="modal.showErrors" class="code-list">
              <li v-for="(error, index) in dryRun.errors" :key="index">{{ error }}</li>
            </ol>
          </li>
          <!-- Codes To Be Created -->
          <li v-if="!failedForErrors(dryRun)">
            <div class="emphasis">{{ dryRun.toCreate.length }}</div>
            completely new {{ dryRun.toCreate.length === 1 ? 'ID' : 'IDs' }} (will be added to
            the database)
            <template v-if="dryRun.toCreate.length">
              <div
                :style="{ display: 'inline' }"
                v-if="modal.showToBeCreated"
                @click="modal.showToBeCreated = false"
                >
                <md-icon>
                  visibility
                </md-icon>
              </div>
              <div :style="{ display: 'inline' }" v-else @click="modal.showToBeCreated = true">
                <md-icon>
                  visibility_off
                </md-icon>
              </div>
            </template>
            <ol v-if="modal.showToBeCreated" class="code-list">
              <li v-for="codeObj in dryRun.toCreate" :key="codeObj.code">{{ codeObj.code }}, {{ codeObj.work_email || "No associated email" }}</li>
            </ol>
          </li>
          <!-- Codes To Be Reactivated -->
          <li v-if="!failedForErrors(dryRun)">
            <div class="emphasis">{{ dryRun.toReactivate.length }}</div>
            {{ dryRun.toReactivate.length === 1 ? 'ID' : 'IDs' }} that were previously inactive
            (will be reinstated as active)
            <template v-if="dryRun.toReactivate.length">
              <div
                :style="{ display: 'inline' }"
                v-if="modal.showToBeReactivated"
                @click="modal.showToBeReactivated = false"
                >
                <md-icon>
                  visibility
                </md-icon>
              </div>
              <div :style="{ display: 'inline' }" v-else @click="modal.showToBeReactivated = true">
                <md-icon>
                  visibility_off
                </md-icon>
              </div>
            </template>
            <ol v-if="modal.showToBeReactivated" class="code-list">
              <li v-for="codeObj in dryRun.toReactivate" :key="codeObj.id">{{ codeObj.code }}, {{ codeObj.work_email || "No associated email" }}</li>
            </ol>
          </li>
          <!-- Codes To Be Updated -->
          <li v-if="!failedForErrors(dryRun)">
            <div class="emphasis">{{ dryRun.toUpdate.length }}</div>
            updated {{ dryRun.toUpdate.length === 1 ? 'ID' : 'IDs' }}
            <template v-if="dryRun.toUpdate.length">
              <div
                :style="{ display: 'inline' }"
                v-if="modal.showToBeUpdated"
                @click="modal.showToBeUpdated = false"
                >
                <md-icon>
                  visibility
                </md-icon>
              </div>
              <div :style="{ display: 'inline' }" v-else @click="modal.showToBeUpdated = true">
                <md-icon>
                  visibility_off
                </md-icon>
              </div>
            </template>
            <ol v-if="modal.showToBeUpdated" class="code-list">
              <li v-for="codeObj in dryRun.toUpdate" :key="codeObj.id">{{ codeObj.code }}, {{ codeObj.work_email || "No associated email" }}</li>
            </ol>
          </li>
          <!-- Codes To Be Ignored -->
          <li v-if="!failedForErrors(dryRun)">
            <div class="emphasis">{{ dryRun.unchangedCodes.length }}</div>
            existing {{ dryRun.unchangedCodes.length === 1 ? 'ID' : 'IDs' }}
            <template v-if="dryRun.unchangedCodes.length">
              <div
                :style="{ display: 'inline' }"
                v-if="modal.showToBeIgnored"
                @click="modal.showToBeIgnored = false"
                >
                <md-icon>
                  visibility
                </md-icon>
              </div>
              <div :style="{ display: 'inline' }" v-else @click="modal.showToBeIgnored = true">
                <md-icon>
                  visibility_off
                </md-icon>
              </div>
            </template>
            <ol v-if="modal.showToBeIgnored" class="code-list">
              <li v-for="codeObj in dryRun.unchangedCodes" :key="codeObj.id">{{ codeObj.code }}, {{ codeObj.work_email || "No associated email" }}</li>
            </ol>
          </li>
        </ul>
        <div v-if="!failedForErrors(dryRun)" class="top-margin">The file you uploaded is missing:</div>
        <ul>
          <!-- Codes To Be Deleted -->
          <li v-if="!failedForErrors(dryRun)">
            <div class="emphasis">{{ dryRun.toDelete.length }}</div>
            {{ dryRun.toDelete.length === 1 ? 'ID' : 'IDs' }} not associated with user accounts
            (will be removed from the database)
            <template v-if="dryRun.toDelete.length">
              <div
                :style="{ display: 'inline' }"
                v-if="modal.showToBeDeleted"
                @click="modal.showToBeDeleted = false"
                >
                <md-icon>
                  visibility
                </md-icon>
              </div>
              <div :style="{ display: 'inline' }" v-else @click="modal.showToBeDeleted = true">
                <md-icon>
                  visibility_off
                </md-icon>
              </div>
            </template>
            <ol v-if="modal.showToBeDeleted" class="code-list">
              <li v-for="codeObj in dryRun.toDelete" :key="codeObj.id">{{ codeObj.code }}, {{ codeObj.work_email || "No associated email" }}</li>
            </ol>
          </li>
          <!-- Codes To Be Deactivated -->
          <li v-if="!failedForErrors(dryRun)">
            <div class="emphasis">{{ dryRun.toDeactivate.length }}</div>
            {{ dryRun.toDeactivate.length === 1 ? 'ID' : 'IDs' }} associated with user accounts
            (will be de-activated)
            <template v-if="dryRun.toDeactivate.length">
              <div
                :style="{ display: 'inline' }"
                v-if="modal.showToBeDeactivated"
                @click="modal.showToBeDeactivated = false"
                >
                <md-icon>
                  visibility
                </md-icon>
              </div>
              <div :style="{ display: 'inline' }" v-else @click="modal.showToBeDeactivated = true">
                <md-icon>
                  visibility_off
                </md-icon>
              </div>
            </template>
            <ol v-if="modal.showToBeDeactivated" class="code-list">
              <li v-for="codeObj in dryRun.toDeactivate" :key="codeObj.id">{{ codeObj.code }}, {{ codeObj.work_email || "No associated email" }}</li>
            </ol>
          </li>
        </ul>
        <div v-if="!failedForErrors(dryRun)" class="top-margin">
          Please confirm to proceed with the changes outlined above.
        </div>
        <div class="buttons-container top-margin">
          <md-button @click="resetViewState" :disabled="isConfirmDisabled">
            Cancel
          </md-button>
          <md-button
            class="confirm-upload proceed-button"
            @click="onActualRun"
            :disabled="isConfirmDisabled"
            v-if="!failedForErrors(dryRun)"
          >
            <i v-if="!actualRun.loading">Confirm</i>
            <span v-else>
              <LoadSpinner :color="'white'" />
            </span>
          </md-button>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { LoadSpinner } from '@/components'
import EmployeeCode from '@/services/employeeCodes'
import CollectionManager from '@/services/collectionManager'
import OrganizationModel from '@/services/organizations'
import Modal from '@/components/Modal'

// this is used to employ resetViewState()
function getDefaultData(nonResettingState = {}) {
  return {
    ...nonResettingState,
    selectedFile: null,
    selectedOrganization: {},
    selectedSubOrganization: {
      name: null,
      id: null,
    },
    modal: {
      isOpen: false,
      showToBeCreated: false,
      showErrors: false,
      showToBeReactivated: false,
      showToBeIgnored: false,
      showToBeUpdated: false,
      showToBeDeleted: false,
      showToBeDeactivated: false,
    },
    dryRunLoading: false,
    dryRun: {},
    actualRun: {
      loading: false,
    },
    errors: null,
  }
}

const nonResettingState = {
  organizations: CollectionManager.create({
    ModelClass: OrganizationModel,
    refreshing: true,
    // filters: { ordering: '-datetime_created' }, TODO(Bruno 4-3-2020): filter alphabetically
  }),
}

export default {
  name: 'EligibilityUploader',
  components: { LoadSpinner, Modal },
  data() {
    return getDefaultData(nonResettingState)
  },
  created() {
    this.fetchOrganizations()
  },
  methods: {
    suborganizations() {
      if (this.user.isStaff) {
        return this.selectedOrganization && this.selectedOrganization.subOrganizations || []
      }

      if (this.user.isAdmin && !this.user.subOrganization) {
        return this.organizationDetails.subOrganizations || []
      }

      return []
    },
    failedForErrors(dryRun) {
      return dryRun && dryRun.errors && dryRun.errors.length > 0
    },
    fetchOrganizations() {
      if (this.user.isStaff) {
        this.organizations.refresh()
      }
    },
    handleTemplateDownload() {
      window.open('/static/employee-codes-template.csv')
    },
    handleUploadClick() {
      this.$refs.file.click()
    },
    handleUpload(e) {
      this.selectedFile = e.target.files[0]
      this.errors = null
    },
    onDryRun() {
      this.dryRunLoading = true

      let options = {
        isStaff: this.user.isStaff,
        organization: this.selectedOrganization.id,
        subOrganizationId: this.selectedSubOrganization.id,
      }
      EmployeeCode.api
        .dryUploadCSV(this.selectedFile, options)
        .then(response => {
          let { errors, unchanged_codes, to_create, to_reactivate, to_update, to_deactivate, to_delete } = response.data
          this.dryRun = {
            toCreate: to_create,
            toReactivate: to_reactivate,
            toUpdate: to_update,
            toDeactivate: to_deactivate,
            toDelete: to_delete,
            unchangedCodes: unchanged_codes,
            errors: errors
          }
          this.modal.isOpen = true
        })
        .catch(this.uploadErrorHandler)
        .finally(() => {
          this.dryRunLoading = false
        })
    },
    onActualRun() {
      this.actualRun.loading = true
      let options = {
        isStaff: this.user.isStaff,
        organization: this.selectedOrganization.id,
        subOrganizationId: this.selectedSubOrganization.id,
      }
      EmployeeCode.api
        .uploadCSV(this.selectedFile, options)
        .then(() => {
          this.success = true
          this.$Alert.alert({
            type: 'success',
            message: `<h4>Import Successful!</h4>`,
            timeout: 6000,
          })
          this.resetViewState()
        })
        .catch(this.uploadErrorHandler)
        .finally(() => {
          this.actualRun.loading = false
        })
    },
    onOrganizationSelection({ target: { value } }) {
      let targetOrg = this.organizations.list.find(org => org.id === value)
      this.selectedOrganization = targetOrg
    },
    onSubOrganizationSelection({ target: { value } }) {
      let targetSubOrg = this.selectedOrganization.subOrganizations.find(subOrg => subOrg.id === value)
      this.selectedSubOrganization = {
        name: targetSubOrg.name,
        id: value,
      }
    },
    resetViewState() {
      this.$refs.file.value = null
      Object.assign(this.$data, getDefaultData())
    },
    uploadErrorHandler(error) {
      // since a 500 error yields error.response.data to be a string,
      // and these strings are very long, we split and slice the first two lines
      // of the error message to display on the page.
      if (error.response.status >= 500) {
        this.errors = error.response.data.split('\n').slice(0, 2)
        return
      }
      // the view expects an array of errors, so the below code confirms that the incoming data conforms.
      if (Array.isArray(error.response.data)) {
        this.errors = error.response.data
      } else {
        this.errors = [error.response.data]
      }
    },
  },
  computed: {
    ...mapState(['user', 'organizationDetails']),
    isUploadDisabled() {
      if (this.modal.isOpen) {
        return true
      }

      if (this.user.isAdmin) {
        return !this.user.adminPermissions.canEditEmployeeCodes
      }

      return false
    },
    isProceedRendered() {
      if (this.user.isAdmin) {
        return !!this.selectedFile
      }

      return this.selectedFile && !!this.selectedOrganization.id
    },
    isProceedDisabled() {
      if (this.modal.isOpen || !this.selectedFile) {
        return true
      }

      if (this.user.isStaff) {
        return !this.selectedOrganization.id
      } else {
        return !this.user.adminPermissions.canEditEmployeeCodes
      }
    },
    isConfirmDisabled() {
      return this.actualRun.loading
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../../styles/variables';
@import '../../styles/mixins/containers';

.eligibility-uploader {
  @include screen-container($mobile-padding: 1rem 0.5rem, $desktop-max-width: 112rem);
  display: flex;
  flex-flow: row;
  align-items: center;
  justify-content: center;
}

.import-card {
  width: 80%;
  margin-left: auto;
  margin-right: auto;
  border: 1px solid #e4eeee;
  border-radius: 5px;
}

.card-header {
  display: flex;
  flex-flow: row;
  align-items: center;
  padding: 0 30px;
  border-bottom: 1px solid #e4eeee;

  h2 {
    margin: 30px auto 30px 0;
    font-weight: 800;
  }

  .md-button {
    background-color: #fff;
    color: #a1a1a6;
    border: solid 1px #a1a1a6;
    border-radius: 5px;
  }
}

.card-body {
  padding: 30px 30px;

  p {
    margin-bottom: 30px;
  }
}

.copy-list {
  margin-bottom: 2rem;
}

.emphasis {
  display: inline;
  text-decoration: underline;
  font-weight: bold;
}

.top-margin {
  margin-top: 1.5rem;
}

.selections-container {
  margin-bottom: 30px;
  display: flex;
  flex-flow: row;
  align-items: center;

  .organizations {
    margin-left: auto;
    width: 300px;
    height: 36px;
    font-family: $base-font-family;
    font-size: 1rem;
  }

  .suborganizations {
    margin-left: auto;
    width: 300px;
    height: 36px;
    font-family: $base-font-family;
    font-size: 1rem;
  }

  .placeholder {
    margin-left: auto;
    width: 300px;
    height: 36px;
    font-family: $base-font-family;
    font-size: 1rem;
    border-radius: 5px;
    border: 1px solid #a6a6a6;
    background-color: #f8f8f8;
    display: flex;
    flex-flow: row;
    align-items: center;
    justify-content: center;
  }
}

.current-file {
  .label {
    margin-right: 25px;
    font-size: 1rem;
  }

  .file-name {
    font-size: 1rem;
  }
}

.errors-container {
  .error {
    color: $ruby;
  }
}

.buttons-container {
  display: flex;
  flex-flow: row;
  align-items: center;

  .choose-file-button {
    margin-right: auto;
    background-color: #000;
    color: #fff;
    border-radius: 5px;

    &:disabled {
      background-color: $mid-gray;
    }
  }

  .confirm-upload {
    margin-left: auto;
    margin-right: 2rem;
  }

  .proceed-button {
    background-color: $blue;
    color: #fff;
    border-radius: 5px;

    &:disabled {
      background-color: $mid-gray;
    }
  }
}

.success-header {
  text-align: center;
  font-weight: 800;
}

.list-container {
  margin-bottom: 30px;

  ul {
    margin-left: 50px;

    li {
      font-family: $base-font-family;
      margin: 5px 0;
    }
  }

  p {
    font-family: $base-font-family;
    margin-left: 50px;
  }
}

.button-container {
  display: flex;
  flex-flow: row;

  .md-button {
    margin-left: auto;
    background-color: #000;
    color: #fff;
    border-radius: 5px;
  }
}

.code-list {
  background-color: $soft-gray;
  border: 1px solid $gray;
  border-radius: 3px;
  margin-top: 0.5rem;
  padding-bottom: 0.5rem;
  padding-left: 1rem;
  max-height: 10rem;
  overflow-y: auto;
}

.bold {
  display: inline;
  font-weight: bold;
}
</style>
